import {API_CONFIG, ROUTES} from '@/utils/constants';
import type {ApiError, ApiResponse, Car, CreditCard, Order, Penalty, User} from '@/types';
import jwt, { Secret } from 'jsonwebtoken'
import CryptoJS from 'crypto-js'

class ApiService {
    private baseUrl: string;
    private version: string;
    private timeout: number;
    private apiUser: string;
    private apiKey: string;

    constructor() {
        this.baseUrl = API_CONFIG.BASE_URL;
        this.version = API_CONFIG.VERSION;
        this.timeout = API_CONFIG.TIMEOUT;
        this.apiUser = API_CONFIG.API_USER;
        this.apiKey = API_CONFIG.API_KEY;
    }

    /**
     * Build full API URL
     */
    private buildUrl(endpoint: string): string {
        return `${this.baseUrl}/api/${this.version}/${endpoint}`;
    }

    /**
     * Make API request with auth token
     */
    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const authToken = this.getAuthToken();

        if (!authToken) {
            throw new Error('No authentication token provided');
        }

        let url = this.buildUrl(endpoint);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        let body = options.body;
        const timestamp = Math.floor(Date.now() / 1000);
        if (options.method && ['POST', 'PUT', 'PATCH'].includes(options.method.toUpperCase())) {

            if (typeof options.body === 'string') {
                try {
                    const bodyData = JSON.parse(options.body);
                    body = JSON.stringify({...bodyData, timestamp});
                } catch {
                    // If body is not JSON, create new object with timestamp
                    body = JSON.stringify({timestamp});
                }
            } else if (options.body) {
                // If body is already an object
                body = JSON.stringify({...options.body, timestamp});
            } else {
                // If no body, create one with timestamp
                body = JSON.stringify({timestamp});
            }
        } else if (!options.method || options.method.toUpperCase() === 'GET') {
            // For GET requests, add timestamp as query parameter
            const urlObj = new URL(url);
            urlObj.searchParams.set('timestamp', timestamp.toString());
            url = urlObj.toString();
        }

        const SECRET_KEY: Secret = this.apiKey;
        const hash = CryptoJS.SHA256(JSON.stringify({timestamp: timestamp.toString()}) + timestamp.toString()).toString();
        const fingerprint = jwt.sign(JSON.stringify(hash), SECRET_KEY)

        try {
            const response = await fetch(url, {
                ...options,
                body,
                headers: {
                    'Authorization': `${authToken}`,
                    'Content-Type': 'application/json',
                    'X-APPCS-API-Login': this.apiUser,
                    'X-APPCS-Signature': fingerprint,
                    ...options.headers,
                },
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const error: ApiError = {
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    status: response.status,
                    errors: errorData.errors,
                };
                throw error;
            }

            return await response.json();
        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new Error('Request timeout');
                }
                throw error;
            }

            throw new Error('An unexpected error occurred');
        }
    }

    /**
     * Get auth token from localStorage or URL params
     */
    private getAuthToken(): string | null {
        // First try to get from localStorage
        const storedToken = localStorage.getItem('user_viewer_auth_token');
        if (storedToken) {
            return storedToken;
        }

        // Then try to get from URL params
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('auth_token');
        if (urlToken) {
            // Store in localStorage for future use
            localStorage.setItem('user_viewer_auth_token', urlToken);
            return urlToken;
        }

        return null;
    }

    /**
     * Set auth token
     */
    public setAuthToken(token: string): void {
        localStorage.setItem('user_viewer_auth_token', token);
    }

    /**
     * Clear auth token
     */
    public clearAuthToken(): void {
        localStorage.removeItem('user_viewer_auth_token');
    }

    /**
     * Check if user is authenticated
     */
    public isAuthenticated(): boolean {
        return !!this.getAuthToken();
    }

    /**
     * Get user data
     */
    public async getUser(): Promise<User> {
        const response = await this.request<ApiResponse<User> | User>(ROUTES.USER_SHOW);

        // Handle different response formats
        if ('user_info' in response) {
            return response.user_info;
        }
        return response as User;
    }

    /**
     * Get penalties
     */
    public async getPenalties(): Promise<Penalty[]> {
        const response = await this.request<ApiResponse<Penalty[]> | Penalty[]>(ROUTES.PENALTY_INDEX);

        // Handle different response formats
        if (Array.isArray(response)) {
            return response;
        }
        if ('data' in response) {
            return response.data;
        }
        return [];
    }

    /**
     * Get parkings
     */
    public async getParkings(): Promise<Order[]> {
        const response = await this.request<ApiResponse<Order[]> | Order[]>(ROUTES.PARKING_INDEX);

        // Handle different response formats
        if (Array.isArray(response)) {
            return response;
        }
        if ('data' in response) {
            return response.data;
        }
        return [];
    }

    /**
     * Get cars
     */
    public async getCars(): Promise<Car[]> {
        const response = await this.request<ApiResponse<Car[]> | Car[]>(ROUTES.CAR_INDEX);

        // Handle different response formats

        if (Array.isArray(response)) {
            return response;
        }
        if ('data' in response) {
            return response.data;
        }
        return [];
    }

    /**
     * Get credit cards
     */
    public async getCreditCards(): Promise<CreditCard[]> {
        const response = await this.request<ApiResponse<CreditCard[]> | CreditCard[]>(ROUTES.CREDIT_CARD_INDEX);

        // Handle different response formats
        if (Array.isArray(response)) {
            return response;
        }
        if ('data' in response) {
            return response.data;
        }
        return [];
    }

    /**
     * Generic method for custom endpoints
     */
    public async get<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint);
    }

    /**
     * Generic method for POST requests
     */
    public async post<T>(endpoint: string, data?: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    /**
     * Generic method for PUT requests
     */
    public async put<T>(endpoint: string, data?: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    /**
     * Generic method for DELETE requests
     */
    public async delete<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'DELETE',
        });
    }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
